import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const TourismCategoryFilter = ({ 
  onFilterChange, 
  activeCategory = 'all',
  className = "" 
}) => {
  const [selectedCategory, setSelectedCategory] = useState(activeCategory);

  const categories = [
    {
      id: 'all',
      label: 'All Offerings',
      icon: 'Grid3X3',
      count: 48,
      description: 'View all tourism services and products',
      color: 'text-primary'
    },
    {
      id: 'accommodation',
      label: 'Accommodation',
      icon: 'Bed',
      count: 18,
      description: 'Hotels, guesthouses, and traditional lodging',
      color: 'text-accent'
    },
    {
      id: 'hospitality',
      label: 'Hospitality',
      icon: 'UtensilsCrossed',
      count: 22,
      description: 'Restaurants, cafes, and local dining experiences',
      color: 'text-success'
    },
    {
      id: 'local-products',
      label: 'Local Products',
      icon: 'ShoppingBag',
      count: 8,
      description: 'Crafts, souvenirs, and regional specialties',
      color: 'text-warning'
    }
  ];

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    onFilterChange(categoryId);
  };

  return (
    <div className={`bg-card rounded-lg shadow-card p-6 mb-8 ${className}`}>
      <div className="mb-6">
        <h2 className="font-heading font-semibold text-xl text-foreground mb-2">
          Tourism Offerings
        </h2>
        <p className="text-muted-foreground">
          Discover authentic local services and products along the heritage trails
        </p>
      </div>
      {/* Desktop Grid Layout */}
      <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {categories?.map((category) => (
          <CategoryCard
            key={category?.id}
            category={category}
            isSelected={selectedCategory === category?.id}
            onClick={() => handleCategoryChange(category?.id)}
          />
        ))}
      </div>
      {/* Mobile Horizontal Scroll */}
      <div className="sm:hidden">
        <div className="flex gap-3 overflow-x-auto momentum-scroll pb-2">
          {categories?.map((category) => (
            <div key={category?.id} className="flex-shrink-0 w-48">
              <CategoryCard
                category={category}
                isSelected={selectedCategory === category?.id}
                onClick={() => handleCategoryChange(category?.id)}
                compact
              />
            </div>
          ))}
        </div>
      </div>
      {/* Active Filter Summary */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <Icon name="Filter" size={16} className="text-muted-foreground" />
            <span className="text-muted-foreground">
              Showing: <span className="font-medium text-foreground">
                {categories?.find(c => c?.id === selectedCategory)?.label}
              </span>
            </span>
          </div>
          <span className="text-muted-foreground">
            {categories?.find(c => c?.id === selectedCategory)?.count} offerings
          </span>
        </div>
      </div>
    </div>
  );
};

const CategoryCard = ({ category, isSelected, onClick, compact = false }) => {
  return (
    <Button
      variant={isSelected ? 'default' : 'outline'}
      onClick={onClick}
      className={`h-auto p-4 flex flex-col items-center text-center space-y-3 transition-all duration-fast card-hover ${
        isSelected ? 'ring-2 ring-primary ring-offset-2' : ''
      }`}
    >
      <div className={`p-3 rounded-full ${
        isSelected 
          ? 'bg-primary-foreground/20' 
          : 'bg-muted'
      }`}>
        <Icon 
          name={category?.icon} 
          size={compact ? 20 : 24} 
          className={isSelected ? 'text-primary-foreground' : category?.color}
        />
      </div>
      <div className="space-y-1">
        <h3 className={`font-medium text-sm ${
          isSelected ? 'text-primary-foreground' : 'text-foreground'
        }`}>
          {category?.label}
        </h3>
        
        {!compact && (
          <p className={`text-xs leading-relaxed ${
            isSelected ? 'text-primary-foreground/80' : 'text-muted-foreground'
          }`}>
            {category?.description}
          </p>
        )}
        
        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          isSelected 
            ? 'bg-primary-foreground/20 text-primary-foreground' 
            : 'bg-muted text-muted-foreground'
        }`}>
          {category?.count} items
        </div>
      </div>
    </Button>
  );
};

export default TourismCategoryFilter;